<script setup lang="ts">
import { uniqueId } from 'es-toolkit/compat'
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue'

const FOCUSABLE_ELEMENTS_QUERY = [
  'a[href]',
  'area[href]',
  'input:not([disabled]):not([type="hidden"]):not([aria-hidden])',
  'select:not([disabled]):not([aria-hidden])',
  'textarea:not([disabled]):not([aria-hidden])',
  'button:not([disabled]):not([aria-hidden])',
  'iframe',
  'object',
  'embed',
  '[contenteditable]',
  '[tabindex]:not([tabindex^="-"])',
].join(', ')

/**
 * Detect iOS devices (iPhone, iPad, iPod)
 * Includes detection for iPads in desktop mode (reports as Mac + has touch)
 * Adapted from rails > bits/device.ts
 */
function isIOS(): boolean {
  const ua = navigator.userAgent.toLowerCase()
  const ipad = ua.includes('ipad') || (ua.includes('macintosh') && 'ontouchend' in document)
  const iphone = ua.includes('iphone')
  const ipod = ua.includes('ipod')
  return ipad || iphone || ipod
}

/**
 * Whether a `FOCUSABLE_ELEMENTS_QUERY` match is a real tab stop. `querySelectorAll`
 * matches `a[href]`/`iframe`/etc. even with `tabindex="-1"`, and viewport-hidden
 * nodes; either as the trap boundary breaks Tab/Shift+Tab cycling.
 */
function isTabbable(element: HTMLElement): boolean {
  const tabindex = element.getAttribute('tabindex')
  if (tabindex !== null && tabindex.startsWith('-')) return false

  // `checkVisibility` accounts for `display: none` ancestors; fall back to the
  // element's own computed display where it isn't supported.
  if (typeof element.checkVisibility === 'function') return element.checkVisibility()
  return getComputedStyle(element).display !== 'none'
}

function isElementContained(rootElement: HTMLElement, activeElement: Element | null): boolean {
  if (!activeElement) return false

  const activeElementRelativePosition = rootElement.compareDocumentPosition(activeElement)
  if (!activeElementRelativePosition) return false

  const isContained = (activeElementRelativePosition & Node.DOCUMENT_POSITION_CONTAINED_BY) > 0
  return isContained
}

const root = ref<HTMLDivElement>()
const lastFocusedElement = ref<Element | null>(document.activeElement)
const focusTrapId = uniqueId('focusTrap')

const afterFocusShiftsFlushed = (callback): void => {
  // Only check and ensure focus is contained after a 1ms `setTimeout`.
  // This is to allow `focus()` calls on child elements to take place first.
  // For that to happen, these calls should happen immediately or by a 0ms `setTimeout` at the latest.
  setTimeout(() => nextTick(callback), 1)
}

const getFocusTarget = (event: KeyboardEvent): HTMLElement | null => {
  const isTabPressed = event.key === 'Tab'
  const isShiftPressed = event.shiftKey

  // Prevent DOM searching if the user is not navigating with the keyboard.
  if (!isTabPressed) return null

  const rootElement = root.value as HTMLElement
  const activeElement = document.activeElement
  const isAlreadyTrapped = isElementContained(rootElement, activeElement)
  const focusableElements = Array.from(
    rootElement.querySelectorAll<HTMLElement>(FOCUSABLE_ELEMENTS_QUERY),
  ).filter(isTabbable)

  const firstFocusableElement = focusableElements[0]
  const lastFocusableElement = focusableElements[focusableElements.length - 1]

  if (isShiftPressed) {
    // Shift + Tab
    if (activeElement === firstFocusableElement || !isAlreadyTrapped) {
      event.preventDefault()
      return lastFocusableElement || rootElement
    }
  } else {
    // Tab
    if (activeElement === lastFocusableElement || !isAlreadyTrapped) {
      event.preventDefault()
      return firstFocusableElement || rootElement
    }
  }

  return null
}

const ensureFocusTrapped = (event: KeyboardEvent): void => {
  const isLatestDialog = allFocusTraps[0] === focusTrapId
  if (!isLatestDialog) return

  const focusTarget = getFocusTarget(event)
  if (focusTarget) focusTarget.focus()
}

onMounted((): void => {
  allFocusTraps.unshift(focusTrapId)

  // Focus dialog first.
  afterFocusShiftsFlushed(() => {
    const rootElement = root.value as HTMLElement
    const previousActiveElement = document.activeElement as HTMLElement | null

    if (rootElement && !isElementContained(rootElement, previousActiveElement)) {
      rootElement.focus({ preventScroll: true })
    }
  })

  window.addEventListener('keydown', ensureFocusTrapped)
})

onBeforeUnmount((): void => {
  const currentFocusTrapIndex = allFocusTraps.indexOf(focusTrapId)

  // Remove current FocusTrap instance from array.
  allFocusTraps.splice(currentFocusTrapIndex, 1)

  /**
   *  Restore focus to latest focused element.
   *  Use preventScroll to avoid iOS Safari scrolling the page when focus is restored,
   *  which can cause navigation elements to shift out of view.
   */
  if (lastFocusedElement.value instanceof HTMLElement) {
    lastFocusedElement.value.focus({ preventScroll: isIOS() })
  }

  window.removeEventListener('keydown', ensureFocusTrapped)
})
</script>

<script lang="ts">
/**
 * We use this array to track all instances of `FocusTrap`.
 * We need to define the array outside the `setup` script
 * since otherwise there will be one array per instance.
 */
const allFocusTraps: any[] = []

export default {}
</script>

<template>
  <div ref="root" tabindex="-1">
    <!-- tabindex="-1" makes the element focusable via JS, but not focusable by the user -->
    <!-- @slot Content to be focus-trapped. -->
    <slot></slot>
  </div>
</template>
